import type { ViewportObserverOptions } from '../types';

type ObserveCallback = () => void;

export class ViewportObserver {
  private io: IntersectionObserver | null = null;
  private callbackMap = new Map<Element, ObserveCallback>();
  private offset: number;
  private destroyed = false;
  private fallbackHandlers: Array<() => void> = [];

  constructor(options: ViewportObserverOptions = {}) {
    this.offset = options.offset ?? 0;

    if (typeof IntersectionObserver !== 'undefined') {
      this.io = new IntersectionObserver(
        entries => this.handleIntersection(entries),
        {
          root: options.root ?? null,
          rootMargin: options.rootMargin ?? `${this.offset}px`,
          threshold: 0,
        },
      );
    }
  }

  observe(element: Element, callback: ObserveCallback): void {
    if (this.destroyed) return;

    this.callbackMap.set(element, callback);

    if (this.io) {
      this.io.observe(element);
    } else {
      this.fallbackCheck(element, callback);
    }
  }

  unobserve(element: Element): void {
    this.callbackMap.delete(element);
    this.io?.unobserve(element);
  }

  private handleIntersection(entries: IntersectionObserverEntry[]): void {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        const callback = this.callbackMap.get(entry.target);
        if (callback) {
          this.unobserve(entry.target);
          callback();
        }
      }
    }
  }

  private fallbackCheck(element: Element, callback: ObserveCallback): void {
    const rect = element.getBoundingClientRect();
    const viewHeight = window.innerHeight || document.documentElement.clientHeight;

    if (
      rect.top - this.offset <= viewHeight &&
      rect.bottom + this.offset >= 0
    ) {
      this.callbackMap.delete(element);
      callback();
    } else {
      const handler = () => {
        if (this.destroyed) {
          window.removeEventListener('scroll', handler);
          window.removeEventListener('resize', handler);
          return;
        }
        const r = element.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.top - this.offset <= vh && r.bottom + this.offset >= 0) {
          window.removeEventListener('scroll', handler);
          window.removeEventListener('resize', handler);
          this.callbackMap.delete(element);
          callback();
        }
      };
      this.fallbackHandlers.push(handler);
      window.addEventListener('scroll', handler, { passive: true });
      window.addEventListener('resize', handler, { passive: true });
    }
  }

  destroy(): void {
    this.destroyed = true;
    this.io?.disconnect();
    this.io = null;
    this.callbackMap.clear();
    for (const handler of this.fallbackHandlers) {
      window.removeEventListener('scroll', handler);
      window.removeEventListener('resize', handler);
    }
    this.fallbackHandlers = [];
  }
}
